import { dayId, keysBetween, newUuidv7 } from '@pf/sync-rules';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import f from '../app/Dialog.module.css';
import { useTripCards } from '../app/data.ts';
import { cx } from '../kit/cx.ts';
import { Button, TripCard } from '../kit/index.ts';
import t from '../kit/type.module.css';
import { flag, today } from '../model/trip.ts';
import { type Edit, write } from '../sync.ts';
import s from './Trips.module.css';

/** The trips index: now, upcoming, past, and a new trip. */
export function Trips() {
  const queries = useQueryClient();
  const trips = useTripCards();
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const now = today();
  const all = [...trips.cards].sort((a, b) => (a.start ?? '9999').localeCompare(b.start ?? '9999'));
  const days = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
  const phase = (x: (typeof all)[number]) =>
    x.archived || (x.end && x.end < now) ? 'past' : x.start && x.start <= now ? 'now' : 'upcoming';
  const groups: [string, typeof all][] = [
    ['Now travelling', all.filter((x) => phase(x) === 'now')],
    ['Upcoming', all.filter((x) => phase(x) === 'upcoming')],
    ['Past', all.filter((x) => phase(x) === 'past')],
  ];
  const status = (x: (typeof all)[number]) => {
    if (!x.start) return 'No dates';
    if (phase(x) === 'upcoming') return `In ${days(now, x.start)} days`;
    if (phase(x) === 'past') return x.start.slice(0, 4);
    return `Day ${String(days(x.start, now) + 1).padStart(2, '0')} / ${String(days(x.start, x.end ?? x.start) + 1).padStart(2, '0')}`;
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    // As PlanEdits.newTrip: one day per date (derived ids, so devices agree) and the Unscheduled list.
    const id = newUuidv7();
    const edits: Edit[] = [
      {
        table: 'trip',
        id,
        tripId: id,
        fields: { title: title.trim(), startDate: start || null, endDate: end || null },
      },
    ];
    const dates: string[] = [];
    if (start && end)
      for (
        let d = new Date(`${start}T00:00:00Z`);
        d.toISOString().slice(0, 10) <= end && dates.length < 366;
        d.setUTCDate(d.getUTCDate() + 1)
      )
        dates.push(d.toISOString().slice(0, 10));
    const keys = keysBetween(null, null, Math.max(dates.length, 1));
    for (const [i, date] of dates.entries())
      edits.push({
        table: 'day',
        id: dayId(id, date),
        tripId: id,
        fields: { date, sortKey: keys[i] ?? 'a0' },
      });
    edits.push({
      table: 'list',
      id: newUuidv7(),
      tripId: id,
      fields: { name: 'Unscheduled', kind: 'unscheduled', sortKey: 'a0' },
    });
    await write(edits);
    setTitle('');
    setStart('');
    setEnd('');
    await queries.invalidateQueries({ queryKey: ['trips'] });
  };

  return (
    <div className={s.page}>
      <h1 className={cx(t.displayL, s.title)}>Trips</h1>
      {trips.isPending && <p className={cx(t.bodyS, s.muted)}>Loading…</p>}
      {trips.cards.length === 0 && !trips.isPending && (
        <p className={cx(t.bodyS, s.muted)}>
          No trips yet. Start one below, or import from Wanderlog in the app.
        </p>
      )}
      {groups.map(([name, list]) =>
        list.length === 0 ? null : (
          <section key={name} aria-label={name}>
            <h2 className={cx(t.label, s.group)}>{name}</h2>
            <ol className={s.list}>
              {list.map((x) => (
                <li key={x.id}>
                  <Link to="/trips/$tripId" params={{ tripId: x.id }} className={s.trip}>
                    <TripCard
                      code={x.code}
                      status={status(x)}
                      title={x.title}
                      start={flag(x.start)}
                      end={flag(x.end)}
                      detail={x.places.join(' · ')}
                      live={phase(x) === 'now'}
                      {...(phase(x) === 'now' ? { todayFlag: flag(now) } : {})}
                    />
                  </Link>
                </li>
              ))}
            </ol>
          </section>
        ),
      )}
      <form onSubmit={create} className={s.form}>
        <h2 className={cx(t.label, s.muted)}>New trip</h2>
        <div className={f.pair}>
          <input
            aria-label="Trip name"
            placeholder="Seoul winter"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className={f.input}
          />
          <input
            aria-label="Start date"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={f.input}
          />
          <input
            aria-label="End date"
            type="date"
            value={end}
            min={start}
            onChange={(e) => setEnd(e.target.value)}
            className={f.input}
          />
          <Button variant="ink" icon={Plus} type="submit">
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
